/**
 * Interceptor global para requisições fetch
 * 
 * Captura erros de rede e CORS causados por sessão expirada
 * e trata de forma apropriada
 */

const DEBUG = import.meta.env.DEV;

// Armazenar referência ao fetch original
const originalFetch = window.fetch;

// Flag para evitar loops infinitos
let isHandlingSessionError = false;

/**
 * Verifica se o erro é relacionado a sessão expirada
 */
function isSessionError(error: any): boolean {
  if (!error) return false;
  
  const errorMessage = error.message?.toLowerCase() || '';
  const errorString = String(error).toLowerCase();
  
  // Padrões que indicam problema de sessão/autenticação
  return (
    errorMessage.includes('failed to fetch') ||
    errorMessage.includes('networkerror') ||
    errorMessage.includes('cors') ||
    errorString.includes('failed to fetch') ||
    errorString.includes('networkerror') ||
    errorString.includes('cors')
  );
}

/**
 * Verifica se a URL é do Supabase e requer autenticação
 */
function isSupabaseAuthRequest(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return (
      urlObj.hostname.includes('supabase.co') &&
      (urlObj.pathname.includes('/rest/') || 
       urlObj.pathname.includes('/auth/') ||
       urlObj.pathname.includes('/storage/'))
    );
  } catch {
    return false;
  }
}

/**
 * Tenta renovar a sessão
 */
async function tryRefreshSession(): Promise<boolean> {
  if (isHandlingSessionError) {
    if (DEBUG) console.log('[FetchInterceptor] Already handling session error');
    return false;
  }

  isHandlingSessionError = true;

  try {
    // Importar dinamicamente para evitar dependência circular
    const { supabase } = await import('@/integrations/supabase/client');
    
    if (DEBUG) console.log('[FetchInterceptor] Attempting to refresh session...');
    
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error || !data.session) {
      console.error('[FetchInterceptor] Session refresh failed:', error);
      
      // Sessão expirou - fazer logout e redirecionar
      await supabase.auth.signOut();
      
      // Mostrar mensagem ao usuário
      const { toast } = await import('sonner');
      toast.error('Sessão expirada', {
        description: 'Por favor, faça login novamente',
        duration: 5000,
      });
      
      // Redirecionar para login após um pequeno delay
      setTimeout(() => {
        window.location.href = '/entrar';
      }, 1000);
      
      return false;
    }
    
    if (DEBUG) console.log('[FetchInterceptor] Session refreshed successfully');
    return true;
  } catch (error) {
    console.error('[FetchInterceptor] Error refreshing session:', error);
    return false;
  } finally {
    // Resetar flag após um delay para permitir novas tentativas
    setTimeout(() => {
      isHandlingSessionError = false;
    }, 5000);
  }
}

/**
 * Wrapper para fetch que intercepta erros de sessão
 */
async function interceptedFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  try {
    const response = await originalFetch(input, init);
    
    // Se a resposta for 401 (Unauthorized) em requisição do Supabase
    if (response.status === 401) {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      
      if (isSupabaseAuthRequest(url)) {
        if (DEBUG) console.log('[FetchInterceptor] 401 Unauthorized on Supabase request');
        
        // Tentar renovar sessão
        const refreshed = await tryRefreshSession();
        
        if (refreshed) {
          // Tentar novamente a requisição original
          if (DEBUG) console.log('[FetchInterceptor] Retrying original request...');
          return originalFetch(input, init);
        }
      }
    }
    
    return response;
  } catch (error) {
    // Capturar erros de rede (CORS, Failed to fetch, etc)
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    
    if (DEBUG) {
      console.error('[FetchInterceptor] Fetch error:', {
        url,
        error,
        message: error instanceof Error ? error.message : String(error),
      });
    }
    
    // Se for erro de sessão em requisição do Supabase
    if (isSessionError(error) && isSupabaseAuthRequest(url)) {
      console.warn('[FetchInterceptor] Session error detected on Supabase request');
      
      // Tentar renovar sessão
      const refreshed = await tryRefreshSession();
      
      if (refreshed) {
        // Tentar novamente a requisição original
        if (DEBUG) console.log('[FetchInterceptor] Retrying original request after session refresh...');
        try {
          return await originalFetch(input, init);
        } catch (retryError) {
          console.error('[FetchInterceptor] Retry failed:', retryError);
          throw retryError;
        }
      }
    }
    
    // Re-lançar o erro original se não conseguiu tratar
    throw error;
  }
}

/**
 * Instala o interceptor global
 */
export function installFetchInterceptor(): void {
  if (window.fetch === interceptedFetch) {
    if (DEBUG) console.log('[FetchInterceptor] Already installed');
    return;
  }
  
  window.fetch = interceptedFetch as typeof fetch;
  
  if (DEBUG) console.log('[FetchInterceptor] Installed successfully');
}

/**
 * Remove o interceptor global (útil para testes)
 */
export function uninstallFetchInterceptor(): void {
  window.fetch = originalFetch;
  
  if (DEBUG) console.log('[FetchInterceptor] Uninstalled');
}
